import { PREFECTURE_OPTIONS } from '@admission/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Globe, Loader2, MapPin, Phone, Save, User } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getGetTenantQueryKey, useGetTenant, useUpdateTenant } from '@/api/generated/tenants';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';
import { useToast } from '@/hooks/use-toast';

// Zod Schema
const basicSettingsSchema = z.object({
  name: z.string().min(1, { message: 'Required' }).max(200),
  domain: z.string().optional(),
  address: z.string().optional(),
  prefecture: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  faculties: z.string().optional(),
  config: z
    .object({
      presidentName: z.string().optional(),
      adminName: z.string().optional(),
    })
    .passthrough(),
});

type BasicSettingsFormValues = z.infer<typeof basicSettingsSchema>;

export default function BasicSettings() {
  const { t } = useTranslation();
  const { selectedTenant, refreshTenant } = useTenant();
  const { toast } = useToast();

  const form = useForm<BasicSettingsFormValues>({
    resolver: zodResolver(basicSettingsSchema),
    defaultValues: {
      name: '',
      domain: '',
      address: '',
      prefecture: '',
      phone: '',
      website: '',
      description: '',
      faculties: '',
      config: {
        presidentName: '',
        adminName: '',
      },
    },
  });

  const { data: result, isLoading: loading } = useGetTenant(selectedTenant?.id || '', {
    query: {
      enabled: !!selectedTenant?.id,
      queryKey: getGetTenantQueryKey(selectedTenant?.id || ''),
    },
  });

  useEffect(() => {
    if (result?.data) {
      const data = result.data;
      form.reset({
        name: data.name || '',
        domain: data.domain || '',
        address: data.address || '',
        prefecture: data.prefecture || '',
        phone: data.phone || '',
        website: data.website || '',
        description: data.description || '',
        faculties: Array.isArray(data.faculties) ? data.faculties.join('\n') : '',
        config: {
          presidentName: data.config?.presidentName || '',
          adminName: data.config?.adminName || '',
        },
      });
    }
  }, [result, form]);

  const updateMutation = useUpdateTenant();

  const onSubmit = async (data: BasicSettingsFormValues) => {
    if (!selectedTenant) return;

    // Transform faculties string to array
    const facultiesArray = data.faculties
      ? data.faculties
          .split('\n')
          .map((s) => s.trim())
          .filter((s) => s !== '')
      : [];

    const payload = {
      ...data,
      faculties: facultiesArray,
    };

    try {
      await updateMutation.mutateAsync({
        id: selectedTenant.id,
        data: payload,
      });

      toast({
        title: t('basic.success'),
        description: 'Tenant configuration has been updated successfully.',
      });
      if (refreshTenant) refreshTenant();
    } catch (error) {
      console.error(error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('common.error.updateFailed', 'Update failed'),
      });
    }
  };

  // Render part changes
  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10">
      {/* Headers ... */}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Identity Section (Keep existing) */}

          {/* Contact Section */}
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <MapPin className="w-5 h-5 text-gray-600" />
                {t('basic.publicInfo.title')}
              </CardTitle>
              <CardDescription>{t('basic.publicInfo.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="prefecture"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('basic.prefecture.label')}</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        >
                          <option value="">{t('basic.prefecture.placeholder')}</option>
                          {PREFECTURE_OPTIONS.map(({ code, name }) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('basic.address')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('basic.placeholders.address')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('basic.phone')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <Input placeholder={t('basic.placeholders.phone')} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('basic.website')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <Input placeholder={t('basic.placeholders.website')} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('basic.introDescription.label')}</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder={t('basic.introDescription.placeholder')}
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('basic.introDescription.description')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="faculties"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('basic.faculties.label')}</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                        placeholder={t('basic.faculties.placeholder')}
                        rows={5}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('basic.faculties.description')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Representatives Section (Keep existing) */}
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <User className="w-5 h-5 text-gray-600" />
                {t('basic.representatives.title')}
              </CardTitle>
              <CardDescription>{t('basic.representatives.description')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="config.presidentName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('basic.president')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('basic.placeholders.presidentName')} {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">{t('basic.representatives.presidentDesc')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="config.adminName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('basic.admin')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('basic.placeholders.adminName')} {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">{t('basic.representatives.adminDesc')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Submit Button (Keep existing) */}
          <div className="flex justify-end pt-4 sticky bottom-4">
            <Button
              type="submit"
              size="lg"
              disabled={form.formState.isSubmitting || loading}
              className="min-w-[180px] shadow-lg"
            >
              {form.formState.isSubmitting || loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loading ? t('basic.loading') : t('basic.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('basic.save')}
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
